<template>
  <div>
    <multiselect
      class="multiselect-employees"
      v-model="selectedEmployees"
      :options="employeeOptions"
      track-by="number"
      :custom-label="employeeLabel"
      v-on:open="onEmployeesDropdownOpen"
      v-on:search-change="onFilterEmployees"
      v-on:select="onSelectEmployee"
      v-on:remove="onSelectEmployee"
      :filter="true"
      :internal-search="true"
      :close-on-select="false"
      :clear-on-select="false"
      :multiple="true"
      :show-labels="false"
      :allow-empty="true"
      :deselectLabel="$t('deselectLabel')"
      selectLabel=""
      selectedLabel=""
      :placeholder="'Mitarbeiter ' + $t('select') + '/suchen'"
    >
      <template slot="singleLabel" slot-scope="{ option }">
        {{ option.firstname }} {{ option.lastname }}
      </template>
      <template slot="option" slot-scope="{ option }">
        {{ option.firstname }} {{ option.lastname }}
      </template>
      <template slot="tag" slot-scope="{ option }">
        <div>{{ option.firstname }} {{ option.lastname }}</div>
      </template>
      <template slot="noResult">
        <div>{{ $t('no_results') }}</div>
      </template>
    </multiselect>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';

export default {
  components: {
    Multiselect,
  },
  props: {
    employees: Array,
    /**
     * Optional date (or ISO string) that determines availability filter (whole-day).
     * If omitted, employees are loaded without the availability filter.
     */
    availableOn: {
      type: [Date, String],
      default: null,
    },
  },
  model: {
    prop: 'employees',
    event: 'select',
  },
  data() {
    return {
      employeesByAvailableOnDate: {},
      selectedEmployees: this.employees,
    };
  },

  computed: {
    ...mapGetters(['getEmployees']),

    availableOnDate() {
      if (!this.availableOn) return '_no_date';
      return this.$dayjs(this.availableOn).format('YYYY-MM-DD');
    },

    employeesAvailableOnDate() {
      return this.employeesByAvailableOnDate[this.availableOnDate] ?? [];
    },

    employeeOptions() {
      const employeeOptionsByNumber = new Map();

      this.employeesAvailableOnDate.forEach((employee) => {
        employeeOptionsByNumber.set(employee.number, employee);
      });

      (this.selectedEmployees ?? []).forEach((employee) => {
        if (!employeeOptionsByNumber.has(employee.number)) {
          employeeOptionsByNumber.set(employee.number, employee);
        }
      });

      return [...employeeOptionsByNumber.values()];
    },
  },

  methods: {
    ...mapActions(['fetchEmployees']),

    /**
     *
     */
    async onSelectEmployee(row) {
      //   console.log('onSelectEmployee', row);
      this.$emit('select', this.selectedEmployees);
    },

    /**
     *
     */
    async loadEmployees() {
      const dateKey = this.availableOnDate;
      if (this.employeesByAvailableOnDate[dateKey]) return;

      const filters = {
        activeState: { value: 'ACTIVE' },
      };

      if (this.availableOn) {
        filters.availableOn = { value: dateKey };
      }

      await this.fetchEmployees({
        page: 0,
        pageSize: 1000,
        sortField: 'lastname',
        sortOrder: 1,
        filters,
      });

      // Vue 2: adding a new object key must use $set to stay reactive
      this.$set(this.employeesByAvailableOnDate, dateKey, [...this.getEmployees]);
    },

    async onEmployeesDropdownOpen() {
      await this.loadEmployees();
    },

    async onFilterEmployees() {
      await this.loadEmployees();
    },

    employeeLabel(employee) {
      const firstName = employee?.firstname ? employee.firstname : '';
      const lastName = employee?.lastname ? employee.lastname : '';
      return `${firstName} ${lastName}`.trim();
    },
  },

  watch: {
    employees(newEmployees, oldEmployees) {
      this.selectedEmployees = newEmployees;
    },
    async availableOn() {
      // If date changes, make sure we load the new date's employees on next open/search.
      // (We don't auto-fetch here to keep it lazy.)
    },
  },
};
</script>

<style scoped lang="scss">
.multiselect__content-wrapper {
  position: absolute;
}

.multiselect-employees {
  min-width: 200px;
  max-width: 300px;
  position: relative;
}
</style>
